import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Cloud, HardDrive, WarningCircle, FolderOpen, Eye, Copy } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const Dashboard = () => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`/api/dashboard`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  if (!data) return <div>Loading...</div>;

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="text-4xl font-black mb-2">DASHBOARD</h1>
        <p className="text-slate-400">Overview of DO Accounts and Quota Usage.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.accounts.map((acc) => (
          <div key={acc.id} className="border border-white/10 bg-card p-6 flex flex-col gap-4">
            <div className="flex justify-between items-start border-b border-white/10 pb-4">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold">{acc.label}</h3>
                {acc.global_full && (
                  <span className="bg-red-500/10 text-red-500 border border-red-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">GLOBAL LIMIT REACHED</span>
                )}
              </div>
              <HardDrive size={24} className="text-primary" />
            </div>
            
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-4">
              {Object.entries(acc.usage || {}).map(([dc, count]) => {
                const isFull = acc.full_datacenters?.includes(dc);
                return (
                  <div key={dc}>
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{dc}</div>
                    <div className="text-2xl font-bold flex items-end gap-1">
                      <span className={count >= acc.limit || isFull ? "text-red-400" : "text-white"}>{isFull ? "FULL" : count}</span>
                      {!isFull && <span className="text-sm text-slate-500 mb-1">/ {acc.limit}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {data.accounts.length === 0 && (
          <div className="border border-dashed border-white/20 bg-transparent p-6 flex items-center justify-center text-slate-500 col-span-full">
            No active DO accounts linked.
          </div>
        )}
      </div>

      <div>
        <h2 className="text-2xl font-bold mb-6">RECENT DEPLOYMENTS</h2>
        <div className="border border-white/10 bg-card overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-white/10 bg-black/20">
                <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Bucket</th>
                <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Account</th>
                <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">DC</th>
                <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Status</th>
                <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">URL</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_deployments.map(dep => (
                <tr key={dep.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="p-4 font-mono text-sm">
                    {dep.bucket_name}
                    {dep.status === 'live' && (
                      <button 
                        onClick={() => navigate(`/do/deployments/${dep.id}/manager`)}
                        className="ml-3 text-xs bg-white/10 hover:bg-primary hover:text-black transition-colors px-2 py-1 font-bold inline-flex items-center gap-1"
                        title="File Manager"
                      >
                        <FolderOpen size={14} /> MGR
                      </button>
                    )}
                  </td>
                  <td className="p-4 text-sm">{dep.account_label}</td>
                  <td className="p-4 text-sm uppercase">{dep.datacenter}</td>
                  <td className="p-4 text-sm">
                    <span className={`px-2 py-1 text-xs font-bold tracking-wider ${
                      dep.status === 'live' ? 'bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/20' : 
                      dep.status === 'failed' ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 
                      'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20'
                    }`}>
                      {dep.status}
                    </span>
                  </td>
                  <td className="p-4 text-sm">
                    {dep.public_url ? (() => {
                      let finalUrl = dep.public_url;
                      if (!finalUrl.startsWith('http')) finalUrl = 'https://' + finalUrl;
                      finalUrl = finalUrl.endsWith('/') ? finalUrl + 'index.html' : (finalUrl.endsWith('/index.html') ? finalUrl : finalUrl + '/index.html');
                      return (
                        <div className="flex items-center gap-2">
                          <div className="bg-[#0A0B0E] border border-white/10 px-2 py-1 text-xs text-slate-400 font-mono truncate w-48 select-all" title={finalUrl}>
                            {finalUrl}
                          </div>
                          <button 
                            onClick={() => { navigator.clipboard.writeText(finalUrl); toast.success("Copied!"); }}
                            className="text-slate-400 hover:text-white transition-colors p-1"
                            title="Copy URL"
                          >
                            <Copy size={16} />
                          </button>
                          <a 
                            href={finalUrl} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="text-slate-400 hover:text-primary transition-colors p-1"
                            title="Preview Site"
                          >
                            <Eye size={16} />
                          </a>
                        </div>
                      );
                    })() : '-'}
                  </td>
                </tr>
              ))}
              {data.recent_deployments.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-500">No deployments yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;